import React, { useEffect, useState } from "react";
import {
  FiUsers,
  FiFileText,
  FiFlag,
  FiPlusCircle,
  FiMessageSquare,
  FiAlertCircle,
  FiActivity,
  FiTrendingUp,
  FiBarChart2,
  FiPieChart,
  FiServer,
  FiRefreshCw,
  FiShield,
  FiPackage,
  FiCreditCard,
  FiTruck,
  FiXCircle,
  FiSettings,
  FiMail,
  FiClock
} from "react-icons/fi";
import StatCard from "../components/StatCard";
import ActionCard from "../components/ActionCard";
import AnalyticsWidget from "../components/AnalyticsWidget";
import AnalyticsChart from "../components/AnalyticsChart";
import TimeframeSelector from "../components/TimeframeSelector";
import OrganizationTable from "../components/OrganizationTable";
import UserManagementTable from "../components/UserManagementTable";
import GoogleSheetsImport from "../components/GoogleSheetsImport";
import JobQueueMonitor from "../components/JobQueueMonitor";
import AdvancedAnalyticsDashboard from "../components/AdvancedAnalyticsDashboard";
import TwoFactorAdminStats from "../components/TwoFactorAdminStats";
import api from "../services/api";
import '../css/AdminDashboardPage.css';

export default function AdminDashboardPage() {
  const [stats, setStats] = useState({
    user_count: 0,
    question_count: 0,
    flagged_reports: 0,
    pending_bug_reports: 0,
    pending_feedback: 0,
    pending_questions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineLoading, setOnlineLoading] = useState(true);
  
  // Enhanced analytics state
  const [timeframe, setTimeframe] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState({
    trends: { users: [], quizzes: [], engagement: [] },
    engagement: null,
    content: null,
    systemHealth: null
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Organizations state
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [orgStatusFilter, setOrgStatusFilter] = useState('all');
  const [orgPagination, setOrgPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [userPagination, setUserPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Digital goods state
  const [digitalGoods, setDigitalGoods] = useState([]);
  const [digitalGoodsLoading, setDigitalGoodsLoading] = useState(false);
  const [digitalGoodsStats, setDigitalGoodsStats] = useState({
    digital_products_count: 0,
    pending_deliveries: 0,
    failed_deliveries: 0,
    delivery_success_rate: 0
  });
  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);

  // Trial configuration state
  const [trialConfigs, setTrialConfigs] = useState([]);
  const [trialConfigsLoading, setTrialConfigsLoading] = useState(false);
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [newTrialConfig, setNewTrialConfig] = useState({
    config_name: '',
    display_name: '',
    description: '',
    trial_duration_days: 14,
    practice_questions_required: 15,
    test_questions_required: 15,
    max_daily_questions: 100,
    ai_requests_allowed: 10,
    is_default: false
  });

  // Fetch dashboard stats (including all badges)
  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/admin/stats");
      setStats(response.data);
    } catch (err) {
      setError("Failed to load dashboard data.");
      console.error("Error loading admin stats:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch online users
  const fetchOnlineUsers = async () => {
    setOnlineLoading(true);
    try {
      const res = await api.get("/admin/online-users");
      setOnlineUsers(res.data || []);
    } catch (e) {
      setOnlineUsers([]);
    } finally {
      setOnlineLoading(false);
    }
  };

  // Fetch comprehensive analytics data
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [trendsRes, engagementRes, contentRes, healthRes] = await Promise.all([
        Promise.all([
          api.get(`/admin/analytics/trends?timeframe=${timeframe}&metric=users`),
          api.get(`/admin/analytics/trends?timeframe=${timeframe}&metric=quizzes`),
          api.get(`/admin/analytics/trends?timeframe=${timeframe}&metric=engagement`)
        ]),
        api.get(`/admin/analytics/engagement?timeframe=${timeframe}`),
        api.get(`/admin/analytics/content?timeframe=${timeframe}`),
        api.get(`/admin/analytics/system-health`)
      ]);

      setAnalyticsData({
        trends: {
          users: trendsRes[0].data,
          quizzes: trendsRes[1].data,
          engagement: trendsRes[2].data
        },
        engagement: engagementRes.data,
        content: contentRes.data,
        systemHealth: healthRes.data
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOnlineUsers();
    fetchAnalytics();
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, [timeframe]);

  // Fetch organizations data
  const fetchOrganizations = async () => {
    setOrganizationsLoading(true);
    try {
      const response = await api.get('/admin/organizations', {
        params: {
          page: orgPagination.page,
          limit: orgPagination.limit,
          search: orgSearchTerm,
          status: orgStatusFilter
        }
      });
      
      setOrganizations(response.data.organizations);
      setOrgPagination(response.data.pagination);
      
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // Handle organization bulk actions
  const handleOrgBulkAction = async (action, organizationIds) => {
    try {
      await api.post('/admin/organizations/bulk-action', {
        action,
        organization_ids: organizationIds
      });
      
      await fetchOrganizations();
      
    } catch (error) {
      console.error('Bulk action failed:', error);
    }
  };

  // Handle organization actions
  const handleViewOrganization = (orgId) => {
    console.log('View organization:', orgId);
  };

  const handleEditOrganization = (org) => {
    console.log('Edit organization:', org);
  };

  const handleDeleteOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) {
      return;
    }
    console.log('Delete organization:', orgId);
  };

  // Fetch users data
  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await api.get('/admin/users/enhanced-search', {
        params: {
          ...userFilters,
          q: userSearchTerm,
          page: userPagination.page,
          limit: userPagination.limit
        }
      });
      
      setUsers(response.data.users || []);
      if (response.data.pagination) {
        setUserPagination(response.data.pagination);
      }
      
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle user bulk actions
  const handleUserBulkAction = async (action, userIds) => {
    try {
      await api.post('/admin/users/bulk-action', {
        action,
        userIds
      });
      
      await fetchUsers();
      
    } catch (error) {
      console.error('User bulk action failed:', error);
    }
  };

  // Handle user actions
  const handleViewUser = (userId) => {
    console.log('View user:', userId);
  };

  const handleEditUser = (user) => {
    console.log('Edit user:', user);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    console.log('Delete user:', userId);
  };

  // Handle tab switching
  // Fetch digital goods data
  const fetchDigitalGoods = async () => {
    setDigitalGoodsLoading(true);
    try {
      const [goodsRes, statsRes, deliveriesRes] = await Promise.all([
        api.get('/admin/entitlements?status=active'),
        api.get('/admin/stats/digital-goods'),
        api.get('/admin/digital-goods/deliveries?limit=10')
      ]);
      
      setDigitalGoods(goodsRes.data.products || []);
      setDigitalGoodsStats(statsRes.data);
      setDeliveries(deliveriesRes.data.deliveries || []);
    } catch (error) {
      console.error('Failed to fetch digital goods:', error);
    } finally {
      setDigitalGoodsLoading(false);
    }
  };

  // Fetch trial configurations
  const fetchTrialConfigs = async () => {
    setTrialConfigsLoading(true);
    try {
      const response = await api.get('/admin/trial-configs');
      setTrialConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Failed to fetch trial configurations:', error);
    } finally {
      setTrialConfigsLoading(false);
    }
  };

  // Handle trial config creation
  const handleCreateTrialConfig = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/trial-configs', newTrialConfig);
      setShowTrialForm(false);
      setNewTrialConfig({
        config_name: '',
        display_name: '',
        description: '',
        trial_duration_days: 14,
        practice_questions_required: 15,
        test_questions_required: 15,
        max_daily_questions: 100,
        ai_requests_allowed: 10,
        is_default: false
      });
      await fetchTrialConfigs();
    } catch (error) {
      console.error('Failed to create trial config:', error);
    }
  };

  // Handle manual digital goods fulfillment
  const handleManualFulfillment = async (deliveryId, notes) => {
    try {
      await api.post(`/admin/digital-goods/fulfill/${deliveryId}`, {
        fulfillment_notes: notes
      });
      await fetchDigitalGoods(); // Refresh data
    } catch (error) {
      console.error('Failed to fulfill digital goods:', error);
    }
  };
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'analytics' && !analyticsData.engagement) {
      fetchAnalytics();
    }
    if (tab === 'organizations' && organizations.length === 0) {
      fetchOrganizations();
    }
    if (tab === 'users' && users.length === 0) {
      fetchUsers();
    }
    if (tab === 'digital-goods' && digitalGoods.length === 0) {
      fetchDigitalGoods();
    }
    if (tab === 'trials' && trialConfigs.length === 0) {
      fetchTrialConfigs();
    }
  };

  // --- Stat cards (top row)
  const statWidgets = [
    {
      title: "Registered Users",
      value: stats.user_count,
      icon: FiUsers,
      accent: "#2c90fc",
    },
    {
      title: "Questions in System",
      value: stats.question_count,
      icon: FiFileText,
      accent: "#3fbb6c",
    },
    {
      title: "Flagged Questions",
      value: stats.flagged_reports,
      icon: FiFlag,
      accent: "#e55b5b",
      badge: stats.flagged_reports > 0 ? stats.flagged_reports : undefined,
    },
    {
      title: "Bug Reports",
      value: stats.pending_bug_reports,
      icon: FiAlertCircle,
      accent: "#ff914d",
      badge: stats.pending_bug_reports > 0 ? stats.pending_bug_reports : undefined,
    },
    {
      title: "Feedback",
      value: stats.pending_feedback,
      icon: FiMessageSquare,
      accent: "#2574e4",
      badge: stats.pending_feedback > 0 ? stats.pending_feedback : undefined,
    },
    {
      title: "Questions to Review",
      value: stats.pending_questions,
      icon: FiFileText,
      accent: "#fa8839",
      badge: stats.pending_questions > 0 ? stats.pending_questions : undefined,
    },
    {
      title: "Active Products",
      value: stats.active_products,
      icon: FiPackage,
      accent: "#9c27b0",
    },
    {
      title: "Transactions Today", 
      value: stats.transactions_today,
      icon: FiCreditCard,
      accent: "#4caf50",
    },
    {
      title: "Pending Deliveries",
      value: stats.pending_deliveries,
      icon: FiTruck,
      accent: "#ff9800",
      badge: stats.pending_deliveries > 0 ? stats.pending_deliveries : undefined,
    },
    {
      title: "Failed Deliveries",
      value: stats.failed_deliveries,
      icon: FiXCircle,
      accent: "#f44336",
      badge: stats.failed_deliveries > 0 ? stats.failed_deliveries : undefined,
    },
    {
      title: "Trial Configs",
      value: stats.active_trial_configs,
      icon: FiSettings,
      accent: "#607d8b",
    },
  ];

  // --- Main dashboard action row (no column/list below!)
  const actionLinks = [
    {
      to: "/admin/reports",
      label: "Flagged Questions",
      icon: FiFlag,
      accent: "#e55b5b",
      badge: stats.flagged_reports > 0 ? stats.flagged_reports : undefined,
    },
    {
      to: "/admin/bug-reports",
      label: "Bug Reports",
      icon: FiAlertCircle,
      accent: "#ff914d",
      badge: stats.pending_bug_reports > 0 ? stats.pending_bug_reports : undefined,
    },
    {
      to: "/admin/feedback",
      label: "Feedback",
      icon: FiMessageSquare,
      accent: "#2574e4",
      badge: stats.pending_feedback > 0 ? stats.pending_feedback : undefined,
    },
    {
      to: "/admin/questions/",
      label: "Questions to Review",
      icon: FiFileText,
      accent: "#fa8839",
      badge: stats.pending_questions > 0 ? stats.pending_questions : undefined,
    },
    {
      to: "/admin/users",
      label: "Manage Users",
      icon: FiUsers,
      accent: "#2c90fc",
    },
    {
      to: "/admin/questions/add",
      label: "Add New Question",
      icon: FiPlusCircle,
      accent: "#3fbb6c",
    },
    {
      to: "/admin/questions",
      label: "Manage Questions",
      icon: FiFileText,
      accent: "#6875f5",
    },
    {
      to: "#",
      label: "Digital Goods",
      icon: FiPackage,
      accent: "#8b5cf6",
      badge: digitalGoodsStats.pending_deliveries > 0 ? digitalGoodsStats.pending_deliveries : undefined,
      onClick: () => setActiveTab('digital-goods')
    },
    {
      to: "#",
      label: "Trial Configs",
      icon: FiSettings,
      accent: "#06b6d4",
      badge: trialConfigs.length,
      onClick: () => setActiveTab('trials')
    },
    {
      to: "#",
      label: "Email Deliveries",
      icon: FiMail,
      accent: "#10b981",
      badge: digitalGoodsStats.failed_deliveries > 0 ? digitalGoodsStats.failed_deliveries : undefined,
      onClick: () => setActiveTab('digital-goods')
    }
  ];;

  return (
    <div className="page-container">
      {/* Enhanced Header with Tab Navigation */}
      <div
        className="admin-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "2rem",
          padding: "1.5rem 0"
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#1F2937' }}>
            Admin Dashboard
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#6B7280', fontSize: '1rem' }}>
            Monitor and manage your platform
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <TimeframeSelector 
            value={timeframe} 
            onChange={setTimeframe}
          />
          <button
            className="refresh-btn"
            onClick={() => {
              fetchStats();
              fetchAnalytics();
            }}
            disabled={loading || analyticsLoading}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            <FiRefreshCw size={16} />
            {loading || analyticsLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        borderBottom: '2px solid #E5E7EB',
        paddingBottom: '1rem'
      }}>
        {[
          { id: 'overview', label: 'Overview', icon: FiBarChart2 },
          { id: 'analytics', label: 'Analytics', icon: FiTrendingUp },
          { id: 'import', label: 'Import', icon: FiPlusCircle },
          { id: 'content', label: 'Content', icon: FiFileText },
          { id: 'users', label: 'Users', icon: FiUsers },
          { id: 'organizations', label: 'Organizations', icon: FiUsers },
          { id: 'digital-goods', label: 'Digital Goods', icon: FiPackage },
          { id: 'trials', label: 'Trial Configs', icon: FiClock },
          { id: 'security', label: 'Security', icon: FiShield },
          { id: 'jobs', label: 'Jobs', icon: FiActivity },
          { id: 'advanced-analytics', label: 'Advanced Analytics', icon: FiTrendingUp },
          { id: 'system', label: 'System Health', icon: FiServer }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              backgroundColor: activeTab === tab.id ? '#3B82F6' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#6B7280',
              transition: 'all 0.2s ease'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div
          className="error-message"
          style={{
            background: "#fff4f4",
            border: "1.5px solid #e55b5b",
            color: "#b72b2b",
            borderRadius: "7px",
            padding: "12px 16px",
            marginBottom: "1em",
          }}
        >
          {error}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {/* Enhanced Stat Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <AnalyticsWidget
              title="Total Users"
              value={stats.user_count}
              icon={FiUsers}
              color="#3B82F6"
              subtitle="Registered platform users"
            />
            <AnalyticsWidget
              title="Active Questions"
              value={stats.question_count}
              icon={FiFileText}
              color="#10B981"
              subtitle="Available in question bank"
            />
            <AnalyticsWidget
              title="Premium Subscribers"
              value={stats.active_subscribers}
              icon={FiTrendingUp}
              color="#8B5CF6"
              suffix=" active"
            />
            <AnalyticsWidget
              title="Pending Reviews"
              value={stats.pending_questions + stats.flagged_reports + stats.pending_bug_reports}
              icon={FiFlag}
              color="#EF4444"
              subtitle="Requiring attention"
            />
          </div>

          {/* Action Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {actionLinks.map((action, i) => (
              <ActionCard key={i} {...action} className="fancy-action-card" />
            ))}
          </div>

          {/* Online Users */}
          <div style={{
            backgroundColor: '#F8FAFC',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #E2E8F0'
          }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#1F2937', fontSize: '1.25rem' }}>
              Users Online Right Now
            </h3>
            {onlineLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>Loading...</div>
            ) : onlineUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                No users currently online.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr key="online-users-header" style={{ borderBottom: '2px solid #E2E8F0' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', color: '#374151', fontWeight: '600' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', color: '#374151', fontWeight: '600' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', color: '#374151', fontWeight: '600' }}>Roles</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onlineUsers.map((u) => (
                      <tr key={u.user_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '0.75rem', color: '#1F2937' }}>
                          {u.first_name} {u.last_name}
                        </td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>{u.email}</td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>
                          {u.roles && u.roles.length ? u.roles.join(", ") : "No roles"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div>
          {analyticsLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              fontSize: '1.125rem',
              color: '#6B7280'
            }}>
              Loading analytics data...
            </div>
          ) : (
            <div>
              {/* Engagement Metrics */}
              {analyticsData.engagement && (
                <div>
                  <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
                    Engagement Analytics
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <AnalyticsWidget
                      title="Active Quiz Users"
                      value={analyticsData.engagement.metrics.active_quiz_users}
                      icon={FiActivity}
                      color="#3B82F6"
                      subtitle={`${timeframe} period`}
                    />
                    <AnalyticsWidget
                      title="Total Quiz Attempts"
                      value={analyticsData.engagement.metrics.total_quiz_attempts}
                      icon={FiBarChart2}
                      color="#10B981"
                      subtitle={`${timeframe} period`}
                    />
                    <AnalyticsWidget
                      title="Average Quiz Score"
                      value={Math.round(analyticsData.engagement.metrics.avg_quiz_score || 0)}
                      suffix="%"
                      icon={FiTrendingUp}
                      color="#8B5CF6"
                      subtitle={`${timeframe} period`}
                    />
                    <AnalyticsWidget
                      title="Weekly Active Users"
                      value={analyticsData.engagement.metrics.weekly_active_users}
                      icon={FiUsers}
                      color="#EF4444"
                      subtitle="Last 7 days"
                    />
                  </div>
                </div>
              )}

              {/* Trend Charts */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                {analyticsData.trends.users?.data && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <AnalyticsChart
                      type="line"
                      data={analyticsData.trends.users.data}
                      xKey="period"
                      yKey="count"
                      title="User Growth Trend"
                      color="#3B82F6"
                      height={250}
                    />
                  </div>
                )}

                {analyticsData.trends.quizzes?.data && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <AnalyticsChart
                      type="bar"
                      data={analyticsData.trends.quizzes.data}
                      xKey="period"
                      yKey="count"
                      title="Quiz Activity Trend"
                      color="#10B981"
                      height={250}
                    />
                  </div>
                )}
              </div>

              {/* User Activity Distribution */}
              {analyticsData.engagement?.activityDistribution && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '2rem'
                }}>
                  <AnalyticsChart
                    type="pie"
                    data={analyticsData.engagement.activityDistribution}
                    xKey="quiz_count_bucket"
                    yKey="user_count"
                    title="User Activity Distribution"
                    height={300}
                    showLegend={true}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'content' && (
        <div>
          {analyticsData.content ? (
            <div>
              <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
                Content Performance
              </h3>
              
              {/* Category Performance */}
              {analyticsData.content.categoryPerformance?.length > 0 && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #E2E8F0',
                  marginBottom: '2rem'
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Category Performance</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                          <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Category</th>
                          <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600' }}>Questions</th>
                          <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600' }}>Attempts</th>
                          <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600' }}>Success Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.content.categoryPerformance.slice(0, 10).map((cat, i) => (
                          <tr key={cat.category_id || `cat-${i}`} style={{ borderBottom: '1px solid #F1F5F9' }}>
                            <td style={{ padding: '0.75rem', fontWeight: '500' }}>{cat.category_name}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{cat.question_count}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>{cat.total_attempts}</td>
                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                              {Math.round(cat.success_rate || 0)}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#6B7280' }}>
              Loading content analytics...
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
            User Management
          </h3>
          
          <UserManagementTable
            users={users}
            loading={usersLoading}
            onView={handleViewUser}
            onEdit={handleEditUser}
            onDelete={handleDeleteUser}
            onBulkAction={handleUserBulkAction}
            pagination={userPagination}
            onPageChange={(newPage) => setUserPagination(prev => ({ ...prev, page: newPage }))}
            searchTerm={userSearchTerm}
            onSearchChange={setUserSearchTerm}
            filters={userFilters}
            onFiltersChange={setUserFilters}
          />
        </div>
      )}

      {activeTab === 'organizations' && (
        <div>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
            Organization Management
          </h3>
          
          <OrganizationTable
            organizations={organizations}
            loading={organizationsLoading}
            onView={handleViewOrganization}
            onEdit={handleEditOrganization}
            onDelete={handleDeleteOrganization}
            onBulkAction={handleOrgBulkAction}
            pagination={orgPagination}
            onPageChange={(newPage) => setOrgPagination(prev => ({ ...prev, page: newPage }))}
            searchTerm={orgSearchTerm}
            onSearchChange={setOrgSearchTerm}
            statusFilter={orgStatusFilter}
            onStatusFilterChange={setOrgStatusFilter}
          />
        </div>
      )}

      {activeTab === 'security' && (
        <div>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
            Security Overview
          </h3>
          
          <TwoFactorAdminStats />
        </div>
      )}

      {activeTab === 'import' && (
        <GoogleSheetsImport />
      )}

      {activeTab === 'jobs' && (
        <JobQueueMonitor />
      )}

      {activeTab === 'advanced-analytics' && (
        <AdvancedAnalyticsDashboard />
      )}

      {activeTab === 'digital-goods' && (
        <div>
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
            Digital Goods Management
          </h3>
          
          {/* Digital Goods Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <AnalyticsWidget
              title="Digital Products"
              value={digitalGoodsStats.digital_products_count}
              icon={FiPackage}
              color="#9c27b0"
              subtitle="Active digital goods"
            />
            <AnalyticsWidget
              title="Pending Deliveries"
              value={digitalGoodsStats.pending_deliveries}
              icon={FiTruck}
              color="#ff9800"
              subtitle="Awaiting delivery"
            />
            <AnalyticsWidget
              title="Failed Deliveries"
              value={digitalGoodsStats.failed_deliveries}
              icon={FiXCircle}
              color="#f44336"
              subtitle="Need admin attention"
            />
            <AnalyticsWidget
              title="Delivery Success Rate"
              value={digitalGoodsStats.delivery_success_rate}
              suffix="%"
              icon={FiMail}
              color="#4caf50"
              subtitle="Overall success rate"
            />
          </div>

          {/* Recent Digital Goods */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #E2E8F0',
            marginBottom: '2rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Recent Digital Products</h4>
            {digitalGoodsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>Loading...</div>
            ) : digitalGoods.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                No digital products found. Create your first digital product to get started.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Product Name</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Delivery Method</th>
                      <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: '600' }}>Price</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {digitalGoods.slice(0, 10).map((product) => (
                      <tr key={product.product_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '0.75rem', fontWeight: '500' }}>{product.display_name}</td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>{product.product_type}</td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>
                          {product.delivery_method?.replace('_', ' ') || 'instant'}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                          ${product.price || 0}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            backgroundColor: product.is_active ? '#dcfce7' : '#fef2f2',
                            color: product.is_active ? '#166534' : '#dc2626'
                          }}>
                            {product.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Deliveries */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #E2E8F0'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Recent Deliveries</h4>
            {deliveriesLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>Loading...</div>
            ) : deliveries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                No recent deliveries found.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Product</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>User</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Date</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map((delivery) => (
                      <tr key={delivery.delivery_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '0.75rem', fontWeight: '500' }}>{delivery.display_name}</td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>{delivery.user_email}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            backgroundColor: delivery.delivery_status === 'delivered' ? '#dcfce7' : 
                                           delivery.delivery_status === 'failed' ? '#fef2f2' : '#fef3c7',
                            color: delivery.delivery_status === 'delivered' ? '#166534' : 
                                  delivery.delivery_status === 'failed' ? '#dc2626' : '#d97706'
                          }}>
                            {delivery.delivery_status}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', color: '#6B7280' }}>
                          {new Date(delivery.purchase_date).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {delivery.delivery_status === 'pending' && (
                            <button
                              onClick={() => handleManualFulfillment(delivery.delivery_id, 'Manual fulfillment by admin')}
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              Fulfill
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'trials' && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#1F2937' }}>
              Trial Configuration Management
            </h3>
            <button
              onClick={() => setShowTrialForm(!showTrialForm)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#3B82F6',
                color: 'white',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <FiPlusCircle size={16} />
              New Trial Config
            </button>
          </div>

          {/* Trial Creation Form */}
          {showTrialForm && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid #E2E8F0',
              marginBottom: '2rem'
            }}>
              <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Create New Trial Configuration</h4>
              <form onSubmit={handleCreateTrialConfig} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Config Name</label>
                  <input
                    type="text"
                    value={newTrialConfig.config_name}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, config_name: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Display Name</label>
                  <input
                    type="text"
                    value={newTrialConfig.display_name}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, display_name: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    required
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Description</label>
                  <textarea
                    value={newTrialConfig.description}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, description: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px',
                      minHeight: '80px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Trial Duration (days)</label>
                  <input
                    type="number"
                    value={newTrialConfig.trial_duration_days}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, trial_duration_days: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    min="1"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Practice Questions Required</label>
                  <input
                    type="number"
                    value={newTrialConfig.practice_questions_required}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, practice_questions_required: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Test Questions Required</label>
                  <input
                    type="number"
                    value={newTrialConfig.test_questions_required}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, test_questions_required: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Max Daily Questions</label>
                  <input
                    type="number"
                    value={newTrialConfig.max_daily_questions}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, max_daily_questions: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    min="1"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>AI Requests Allowed</label>
                  <input
                    type="number"
                    value={newTrialConfig.ai_requests_allowed}
                    onChange={(e) => setNewTrialConfig({...newTrialConfig, ai_requests_allowed: parseInt(e.target.value)})}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px'
                    }}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '500' }}>
                    <input
                      type="checkbox"
                      checked={newTrialConfig.is_default}
                      onChange={(e) => setNewTrialConfig({...newTrialConfig, is_default: e.target.checked})}
                    />
                    Set as Default Configuration
                  </label>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={() => setShowTrialForm(false)}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #E2E8F0',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Create Configuration
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Trial Configurations Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid #E2E8F0'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Trial Configurations</h4>
            {trialConfigsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>Loading...</div>
            ) : trialConfigs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6B7280' }}>
                No trial configurations found. Create the first one to get started.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                      <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: '600' }}>Configuration</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Duration</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Practice Qs</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Test Qs</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Daily Limit</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>AI Requests</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Default</th>
                      <th style={{ textAlign: 'center', padding: '0.75rem', fontWeight: '600' }}>Active Overrides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trialConfigs.map((config) => (
                      <tr key={config.config_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                        <td style={{ padding: '0.75rem' }}>
                          <div style={{ fontWeight: '500' }}>{config.display_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{config.config_name}</div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.trial_duration_days}d</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.practice_questions_required}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.test_questions_required}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.max_daily_questions}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.ai_requests_allowed}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {config.is_default && (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              backgroundColor: '#dcfce7',
                              color: '#166534'
                            }}>
                              Default
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{config.active_overrides || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'system' && (
        <div>
          {analyticsData.systemHealth ? (
            <div>
              <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.5rem', color: '#1F2937' }}>
                System Health
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                <AnalyticsWidget
                  title="Database Connections"
                  value={analyticsData.systemHealth.systemMetrics.db_connections}
                  icon={FiServer}
                  color="#3B82F6"
                  subtitle="Active connections"
                />
                <AnalyticsWidget
                  title="Errors (24h)"
                  value={analyticsData.systemHealth.systemMetrics.bugs_24h + analyticsData.systemHealth.systemMetrics.reports_24h}
                  icon={FiAlertCircle}
                  color="#EF4444"
                  subtitle="Bugs and reports"
                />
                <AnalyticsWidget
                  title="Active Users (1h)"
                  value={analyticsData.systemHealth.systemMetrics.active_users_hour}
                  icon={FiActivity}
                  color="#10B981"
                  subtitle="Last hour"
                />
                <AnalyticsWidget
                  title="Quizzes (1h)"
                  value={analyticsData.systemHealth.systemMetrics.quizzes_last_hour}
                  icon={FiBarChart2}
                  color="#8B5CF6"
                  subtitle="Last hour"
                />
              </div>

              {/* Peak Usage Chart */}
              {analyticsData.systemHealth.peakUsage?.length > 0 && (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <AnalyticsChart
                    type="bar"
                    data={analyticsData.systemHealth.peakUsage}
                    xKey="hour"
                    yKey="quiz_count"
                    title="Peak Usage Hours (Last 7 Days)"
                    color="#3B82F6"
                    height={250}
                  />
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#6B7280' }}>
              Loading system health data...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
